import LocationOnIcon from "@mui/icons-material/LocationOn";
import type { AutocompleteInputChangeReason, AutocompleteRenderInputParams } from "@mui/material";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { getValueOrDraft } from "components/Beneficiar/utils";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { formatAddress } from "helpers/utils";
import { useDebounce } from "hooks/useDebounce";
import { forwardRef, useEffect, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppSelector } from "store/store";
import type { ITransferee } from "store/transfereesStore";

import variables from "../../../assets/styles/_variables.module.scss";
import AutocompleteFormComponent from "../FormComponent/AutocompleteFormComponent";
import StyledTextField from "../StyledTextField/StyledTextField";

interface IAddressAutocomplete {
    control: any;
    setValue: UseFormSetValue<any> | undefined; // Typing different than any causes incompatibility issues. Not sure why.
    streetValue: string | TAddressAutococompleteOption | undefined;
    error: any;
    required?: any;
}

export type TAddressAutococompleteOption = {
    id: number;
    street: string;
    zipCode: string;
    city: string;
    country: string;
};

/**
 * Returns formatted option for saved address in beneficiary's data.
 */
export const getAddressEditOption = (
    element: IBeneficiar | IRepresentative | ITransferee | undefined,
): TAddressAutococompleteOption => {
    return {
        id: -1,
        street: getValueOrDraft(element?.street ?? ""),
        zipCode: element?.zipCode ?? "",
        city: element?.city ?? "",
        country: element?.country ?? "",
    };
};

const AddressAutocomplete = forwardRef<any, IAddressAutocomplete>(function AddressAutocomplete(
    props: IAddressAutocomplete,
    ref,
) {
    const [t, i18n] = useTranslation();
    const { control, setValue, streetValue, error, required } = props;
    const { userData } = useAppSelector((state) => state.settings);

    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [options, setOptions] = useState<TAddressAutococompleteOption[]>([]);
    const [searchingResults, setSearchingResults] = useState(false);
    const [chosenAddress, setChosenAddress] = useState<string>("");

    // Opens options only if something has been typed.
    useEffect(() => {
        if (searchValue.length) {
            setOpen(true);
        } else {
            setOptions([]);
            setOpen(false);
        }
    }, [searchValue]);

    const debouncedSearchValue = useDebounce(searchValue, 1000);

    // Triggers debounce update.
    useEffect(() => {
        if (debouncedSearchValue) {
            fetchMapboxResults();
            setChosenAddress(debouncedSearchValue);
        }
    }, [debouncedSearchValue]);

    /**
     * Formats suggestion to create options for the autocomplete with the fiels we need.
     * @param suggestions Suggestions returned by Mapbox.
     * @returns Array with structured options.
     */
    const formatSuggestions = (suggestions: Record<string, any>[]): TAddressAutococompleteOption[] => {
        return suggestions.map((item, index) => {
            const data = item.context;
            return {
                id: index,
                street: data.address ? data.address.name ?? "" : data.street ? data.street.name : "",
                zipCode: data.postcode ? data.postcode.name : "",
                city: data.place ? data.place.name : "",
                country: data.country ? data.country.name : "",
            };
        });
    };

    /**
     * Fetches results from Mapbox.
     */
    const fetchMapboxResults = async () => {
        const sessionToken = userData?.uuid;
        setOptions([]);
        setSearchingResults(true);
        try {
            // Added France coordinates.
            const response = await axios.get(
                `https://api.mapbox.com/search/searchbox/v1/suggest?q=${debouncedSearchValue}&access_token=${
                    import.meta.env.REACT_APP_MAPBOX_TOKEN
                }&session_token=${sessionToken}&language=${i18n.language}&types=address&proximity=1.8883335,46.603354`,
            );
            if (response.data.suggestions.length > 0) {
                // If it has found suggestions, format them for the options component.
                await setOptions(formatSuggestions(response.data.suggestions));
                setSearchingResults(false);
            } else {
                await setOptions([]);
                setSearchingResults(false);
            }
        } catch (error: any) {
            toast.error(t("AddressAutocomplete.SearchError") as string);
            setSearchingResults(false);
        }
    };

    const renderAddressInput = (params: AutocompleteRenderInputParams) => {
        return (
            <StyledTextField
                {...params}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t("AddressPlaceholders.Street")}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {searchingResults ? (
                                <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />
                            ) : (
                                params.InputProps.endAdornment
                            )}
                        </>
                    ),
                }}
            />
        );
    };

    const renderAddressOptions = (props: object, option: TAddressAutococompleteOption) => {
        return (
            <div
                {...props}
                onClick={() => handleChooseOption(option)}
                key={`address-${option.id}`}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
                <LocationOnIcon sx={{ height: "15px", width: "15px", color: variables["foreground"] }} />
                <span>
                    {formatAddress({
                        street: option.street,
                        city: option.city,
                        zipCode: option.zipCode,
                        country: option.country,
                    })}
                </span>
            </div>
        );
    };

    const handleInputChange = (reason: AutocompleteInputChangeReason, e: any) => {
        if (reason === "input") {
            if (setValue) {
                setValue(
                    "street",
                    { ...(streetValue as TAddressAutococompleteOption), street: e?.target?.value ?? "" },
                    { shouldDirty: true },
                );
            }
        }
        if (reason === "clear") {
            setOptions([]);
            setSearchValue("");
            setChosenAddress("");
            setSearchingResults(false);

            if (setValue) {
                setValue("street", "");
                setValue("zipCode", "");
                setValue("city", "");
                setValue("country", { name: "", flag: "" });
            }
        }
    };

    /**
     * Set fields values of chosen address option
     * @param option Address options
     */
    const handleChooseOption = (option: TAddressAutococompleteOption) => {
        if (!setValue) return;
        setValue("street", option);
        setValue("zipCode", option.zipCode);
        setValue("city", option.city);
        setValue("country", { name: option.country, flag: "" });
        setOpen(false);
    };

    return (
        <AutocompleteFormComponent
            label={t("NewBeneficiary.StreetNumber")}
            options={options}
            renderOptionText={(option: TAddressAutococompleteOption) => option.street ?? [searchValue]}
            renderOption={renderAddressOptions}
            ref={ref}
            renderInput={renderAddressInput}
            isOptionEqualToValue={(option, value) => option.street === value.street}
            loading={searchingResults}
            onInputChange={(e, data, reason) => handleInputChange(reason, e)}
            control={control}
            controlName="street"
            open={open}
            onClose={() => setOpen(false)}
            noOptionsText={t("AddressAutocomplete.NoSuggestions")}
            error={error}
            required={required}
        />
    );
});

export default AddressAutocomplete;
